// Liquidaciones / actas de finiquito (Arts. 169, 184-188 CT).
// Nómina → Salidas. El motor vive en la BD (nom_liquidacion_generar, script 164);
// cerrar termina el contrato, descuenta anticipos y salda préstamos.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Banknote, BookOpen, Loader2, Lock, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Imprimible, PrintButton } from '@/components/print/DocPrint';
import { getErrorMessage } from '@/lib/api/client';
import { ExportarBotones, type Celda } from '@/lib/utils/exportarTabla';
import {
  Campo, Modal, fecha, fieldCls, money, nomRpc, CAUSAS_SALIDA, useDialogoEnLinea,
  type EmpleadoRow, type LiquidacionData, type LiquidacionRow, hoyISO } from './shared';
import { EstadoRol } from './Rol';
import { fechaLocal } from '@/lib/utils/fecha';

function DetalleLiquidacion({ selId, volver }: { selId: string; volver: () => void }) {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data, isFetching } = useQuery<LiquidacionData>({
    queryKey: ['nom-liquidacion', selId],
    queryFn: () => nomRpc<LiquidacionData>('nom_liquidacion', { _id: selId }),
    staleTime: 0,
  });
  const l = data?.liquidacion ?? null;

  const invalidar = () => {
    void qc.invalidateQueries({ queryKey: ['nom-liquidaciones'] });
    void qc.invalidateQueries({ queryKey: ['nom-liquidacion'] });
    void qc.invalidateQueries({ queryKey: ['nom-empleados'] });
    void qc.invalidateQueries({ queryKey: ['nom-contratos'] });
  };
  const recalcular = useMutation({
    mutationFn: () => nomRpc('nom_liquidacion_generar', { _empleado_id: l!.empleado_id, _causa: l!.causa, _fecha_salida: l!.fecha_salida }),
    onSuccess: () => { toast.success('Liquidación recalculada'); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const cerrar = useMutation({
    mutationFn: () => nomRpc('nom_liquidacion_cerrar', { _id: selId }),
    onSuccess: () => { toast.success('Liquidación cerrada: contrato terminado, anticipos y préstamos saldados'); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const pagar = useMutation({
    mutationFn: () => nomRpc('nom_liquidacion_pagar', { _id: selId, _fecha: hoyISO() }),
    onSuccess: () => { toast.success('Liquidación pagada'); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: () => nomRpc('nom_liquidacion_eliminar', { _id: selId }),
    onSuccess: () => { toast.success('Borrador eliminado'); volver(); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const contabilizar = useMutation({
    mutationFn: () => nomRpc<{ numero: number; reemplazado: boolean }>('nom_liquidacion_contabilizar', { _id: selId }),
    onSuccess: (r) => {
      toast.success(`Asiento contable N.º ${r.numero} ${r.reemplazado ? 'regenerado' : 'creado'} (Contabilidad → Asientos)`);
      invalidar();
      void qc.invalidateQueries({ queryKey: ['nom-liquidacion'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (isFetching && !data) return <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>;
  if (!l) return null;
  const ingresos = data!.detalles.filter((d) => d.tipo === 'ingreso');
  const egresos = data!.detalles.filter((d) => d.tipo === 'egreso');

  return (
    <div className="space-y-4">
      {dlg.ui}
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="outline" onClick={volver}><ArrowLeft className="w-4 h-4 mr-1" /> Volver</Button>
        <h1 className="text-xl font-semibold">Acta de finiquito <span className="font-mono text-sm text-muted-foreground">{l.codigo}</span></h1>
        <EstadoRol estado={l.estado} />
        <div className="flex-1" />
        {l.estado === 'borrador' && (
          <>
            <Button variant="outline" isLoading={recalcular.isPending} onClick={() => recalcular.mutate()}><RefreshCw className="w-4 h-4 mr-1" /> Recalcular</Button>
            <Button isLoading={cerrar.isPending} onClick={async () => { if (await dlg.confirmar('¿Cerrar la liquidación?', { detalle: 'Cerrar termina el contrato, descuenta anticipos y salda préstamos.', aceptar: 'Cerrar' })) cerrar.mutate(); }}><Lock className="w-4 h-4 mr-1" /> Cerrar</Button>
            <Button variant="destructive" isLoading={eliminar.isPending} onClick={async () => { if (await dlg.confirmar('¿Eliminar el borrador?', { peligro: true, aceptar: 'Eliminar' })) eliminar.mutate(); }}><Trash2 className="w-4 h-4" /></Button>
          </>
        )}
        {l.estado === 'cerrado' && (
          <Button isLoading={pagar.isPending} onClick={async () => { if (await dlg.confirmar('¿Marcar como pagada?', { aceptar: 'Marcar pagada' })) pagar.mutate(); }}><Banknote className="w-4 h-4 mr-1" /> Marcar pagada</Button>
        )}
        {l.estado !== 'borrador' && (
          <>
            <Button variant="outline" isLoading={contabilizar.isPending}
              onClick={async () => {
                const ya = !!data?.asiento;
                const ok = await dlg.confirmar(ya ? '¿Regenerar el asiento?' : '¿Contabilizar el finiquito?', {
                  detalle: ya
                    ? `Ya está contabilizado en el asiento N.º ${data!.asiento!.numero}. Regenerarlo reemplaza el asiento.`
                    : 'Se creará el asiento del finiquito (gasto sueldos + consumo de provisiones + indemnizaciones). Requiere el mapeo de cuentas en Configuración Contable.',
                  aceptar: ya ? 'Regenerar' : 'Contabilizar',
                });
                if (ok) contabilizar.mutate();
              }}>
              <BookOpen className="w-4 h-4 mr-1" /> {data?.asiento ? 'Re-contabilizar' : 'Contabilizar'}
            </Button>
            {data?.asiento && (
              <span className="self-center px-2 py-0.5 rounded-full text-xs bg-completado-soft text-completado">
                Asiento N.º {data.asiento.numero}
              </span>
            )}
          </>
        )}
        <PrintButton />
      </div>

      {(data?.avisos ?? []).map((aviso, i) => (
        <div key={i} className="border border-urgente bg-urgente-soft text-urgente rounded-lg p-4 text-sm font-medium">
          ⚠ {aviso}
        </div>
      ))}

      <Imprimible titulo="Acta de Finiquito" subtitulo={`${l.codigo ?? ''} — ${fecha(l.fecha_salida)}`}>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm">
            <div><span className="text-muted-foreground">Trabajador: </span>{l.razon_social}</div>
            <div><span className="text-muted-foreground">Cédula: </span><span className="font-mono">{l.identificacion}</span></div>
            <div><span className="text-muted-foreground">Cargo: </span>{l.cargo ?? '—'}</div>
            <div><span className="text-muted-foreground">Contrato: </span>{l.tipo_contrato} desde {fecha(l.fecha_inicio)}</div>
            <div><span className="text-muted-foreground">Causa: </span>{CAUSAS_SALIDA[l.causa] ?? l.causa}</div>
            <div><span className="text-muted-foreground">Fecha de salida: </span>{fecha(l.fecha_salida)}</div>
            <div><span className="text-muted-foreground">Años de servicio: </span>{Number(l.anios_servicio).toFixed(2)}</div>
            <div><span className="text-muted-foreground">Última remuneración: </span><span className="font-mono">{money(l.ultima_remuneracion)}</span></div>
          </div>

          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Concepto</th><th className="py-2 pr-3 text-right">Valor</th>
            </tr></thead>
            <tbody>
              {ingresos.map((d, i) => (
                <tr key={i} className="border-b border-border/50">
                  <td className="py-1.5 pr-3">{d.descripcion}</td>
                  <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{money(d.valor)}</td>
                </tr>
              ))}
              <tr className="border-b font-semibold"><td className="py-1.5 pr-3 text-right">Total ingresos</td><td className="py-1.5 pr-3 text-right font-mono tabular-nums">{money(l.total_ingresos)}</td></tr>
              {egresos.map((d, i) => (
                <tr key={i} className="border-b border-border/50">
                  <td className="py-1.5 pr-3">{d.descripcion}</td>
                  <td className="py-1.5 pr-3 text-right font-mono tabular-nums text-urgente">−{money(d.valor)}</td>
                </tr>
              ))}
              {egresos.length > 0 && <tr className="border-b font-semibold"><td className="py-1.5 pr-3 text-right">Total egresos</td><td className="py-1.5 pr-3 text-right font-mono tabular-nums">−{money(l.total_egresos)}</td></tr>}
              <tr className="text-base font-bold"><td className="py-2 pr-3 text-right">NETO A RECIBIR</td><td className="py-2 pr-3 text-right font-mono tabular-nums">{money(l.total_neto)}</td></tr>
            </tbody>
          </table>

          {l.observaciones && <p className="text-sm text-muted-foreground">{l.observaciones}</p>}

          <div className="grid grid-cols-2 gap-8 pt-12 text-center text-sm">
            <div className="border-t pt-2">EL EMPLEADOR</div>
            <div className="border-t pt-2">EL TRABAJADOR<br /><span className="font-mono text-xs">{l.identificacion}</span></div>
          </div>
        </div>
      </Imprimible>
    </div>
  );
}

export function Liquidaciones() {
  const qc = useQueryClient();
  const [estado, setEstado] = useState('todos');
  const [selId, setSelId] = useState<string | null>(null);
  const { data: rows = [], isFetching } = useQuery<LiquidacionRow[]>({
    queryKey: ['nom-liquidaciones', estado],
    queryFn: () => nomRpc<LiquidacionRow[]>('nom_liquidaciones', { _estado: estado }),
    staleTime: 0,
  });
  const { data: empleados = [] } = useQuery<EmpleadoRow[]>({
    queryKey: ['nom-empleados-opt'],
    queryFn: () => nomRpc<EmpleadoRow[]>('nom_empleados', { _q: null, _solo_activos: false }),
  });

  const [abierto, setAbierto] = useState(false);
  const [form, setForm] = useState({ empleado_id: '', causa: 'desahucio', fecha_salida: hoyISO(), observaciones: '' });

  const generar = useMutation({
    mutationFn: () => nomRpc<LiquidacionData>('nom_liquidacion_generar', {
      _empleado_id: form.empleado_id, _causa: form.causa, _fecha_salida: form.fecha_salida,
      _observaciones: form.observaciones || null,
    }),
    onSuccess: (data) => {
      toast.success('Liquidación generada (borrador)'); setAbierto(false);
      void qc.invalidateQueries({ queryKey: ['nom-liquidaciones'] });
      if (data.liquidacion) setSelId(data.liquidacion.id);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (selId) {
    return <div className="p-6"><DetalleLiquidacion selId={selId} volver={() => setSelId(null)} /></div>;
  }

  const headersExp = ['N°', 'Código', 'Empleado', 'Causa', 'Salida', 'Años', 'Neto', 'Estado'];
  const filasExport: Celda[][] = [
    ...rows.map((r, i): Celda[] => [
      i + 1, r.codigo, r.razon_social, (CAUSAS_SALIDA[r.causa] ?? r.causa).split(' (')[0],
      fecha(r.fecha_salida), Number(r.anios_servicio), Number(r.total_neto), r.estado,
    ]),
    ['', '', '', '', '', 'TOTAL', rows.reduce((a, r) => a + Number(r.total_neto), 0), ''],
  ];

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Liquidaciones y finiquitos</h1>
        <div className="flex items-center gap-2">
          <ExportarBotones nombre={`liquidaciones-${fechaLocal()}`} titulo="Liquidaciones y finiquitos"
            headers={headersExp} filas={filasExport} colsDerecha={[0, 5, 6]}
            fmtPDF={(v, c) => (c === 6 ? (v === '' ? '' : money(Number(v))) : String(v ?? ''))}
            disabled={rows.length === 0} />
          <Button onClick={() => setAbierto(true)}><Plus className="w-4 h-4 mr-1" /> Nueva liquidación</Button>
        </div>
      </div>

      <select className={`${fieldCls} w-56`} value={estado} onChange={(e) => setEstado(e.target.value)} aria-label="Estado">
        <option value="todos">Todas</option>
        <option value="borrador">Borradores</option>
        <option value="cerrado">Cerradas</option>
        <option value="pagado">Pagadas</option>
      </select>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Liquidaciones ({rows.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Código</th><th className="py-2 pr-3">Empleado</th><th className="py-2 pr-3">Causa</th>
              <th className="py-2 pr-3">Salida</th><th className="py-2 pr-3 text-right">Años</th>
              <th className="py-2 pr-3 text-right">Neto</th><th className="py-2 pr-3">Estado</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && !isFetching && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Sin liquidaciones</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className="border-b last:border-0 hover:bg-muted/40 cursor-pointer" onClick={() => setSelId(r.id)}
                    role="button" tabIndex={0} onKeyDown={(ev) => { if (ev.key === 'Enter') setSelId(r.id); }}>
                  <td className="py-2 pr-3 font-mono">{r.codigo}</td>
                  <td className="py-2 pr-3">{r.razon_social}<div className="text-xs text-muted-foreground font-mono">{r.identificacion}</div></td>
                  <td className="py-2 pr-3">{(CAUSAS_SALIDA[r.causa] ?? r.causa).split(' (')[0]}</td>
                  <td className="py-2 pr-3 font-mono">{fecha(r.fecha_salida)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{Number(r.anios_servicio).toFixed(1)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums font-semibold">{money(r.total_neto)}</td>
                  <td className="py-2 pr-3"><EstadoRol estado={r.estado} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title="Nueva liquidación" onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <Campo label="Empleado *">
              <select className={fieldCls} value={form.empleado_id} onChange={(e) => setForm({ ...form, empleado_id: e.target.value })}>
                <option value="">— Selecciona —</option>
                {empleados.map((e) => <option key={e.id} value={e.id}>{e.razon_social} ({e.identificacion})</option>)}
              </select>
            </Campo>
            <Campo label="Causa de terminación (Art. 169 CT) *">
              <select className={fieldCls} value={form.causa} onChange={(e) => setForm({ ...form, causa: e.target.value })}>
                {Object.entries(CAUSAS_SALIDA).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Campo>
            <Campo label="Fecha de salida *"><input type="date" className={fieldCls} value={form.fecha_salida} onChange={(e) => setForm({ ...form, fecha_salida: e.target.value })} /></Campo>
            <Campo label="Observaciones"><input className={fieldCls} value={form.observaciones} onChange={(e) => setForm({ ...form, observaciones: e.target.value })} /></Campo>
            <div className="text-xs text-muted-foreground">
              Se genera un borrador con: remuneración pendiente, décimos proporcionales, vacaciones no gozadas,
              bonificación del 25% (Art. 185) e indemnización (Art. 188) según la causa, menos anticipos y préstamos.
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={generar.isPending} disabled={!form.empleado_id || !form.fecha_salida} onClick={() => generar.mutate()}>Generar borrador</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
